<template>
  <section>
    <ValidationObserver v-slot="{ valid }">
      <a-card v-if="enableVeriff" :title="$t('Identification')">
        <div :id="veriffId"></div>
      </a-card>
      <a-card v-else :title="$t('Identification')">
        <h4>{{ $t("document_content") }}</h4>
        <a-row>
          <a-col :span="12">
            <ValidationProvider
              :name="$t('document_content')"
              rules="required"
              mode="eager"
              :immediate="immediate"
              v-slot="slotProps"
            >
              <a-form-item
                has-feedback
                :validate-status="resolveState(slotProps)"
                :help="slotProps.errors[0]"
              >
                <a-input
                  :size="$store.state.clientInfo.uiSize"
                  :placeholder="$t('Passport Placeholder')"
                  v-model="passport"
                />
              </a-form-item>
            </ValidationProvider>
          </a-col>
        </a-row>
        <p></p>
        <a-row :gutter="16">
          <a-col :span="24">
            <h4>{{ $t("Proof of Identity") }}</h4>
            <h5>
              {{ $t("identity_subtitle") }}
            </h5>
            <a-radio-group
              v-model="identityType"
              button-style="solid"
              size="large"
            >
              <a-radio value="passport">
                {{ $t("International Passport") }}
              </a-radio>
              <a-radio value="dl">
                {{ $t("Driver License") }}
              </a-radio>
            </a-radio-group>
            <p></p>
            <div v-if="identityType === 'passport'">
              <upload-files
                :size="uiSize"
                :file.sync="fileList4Identity"
                proofOfIdentity="ProofOfIdentity"
                fileType="individual_id_passport"
              ></upload-files>
              <photo-example />
            </div>
            <div v-else>
              <a-row type="flex" justify="start">
                <a-col :span="2">
                  <h5>{{ $t("Front Side") }}</h5>
                </a-col>
                <a-col :span="22">
                  <upload-files
                    :size="uiSize"
                    :file.sync="fileList4FrontDriverLicense"
                    proofOfIdentity="ProofOfIdentity"
                    fileType="individual_id_dl_front"
                  ></upload-files>
                </a-col>
              </a-row>
              <br />
              <a-row type="flex" justify="start">
                <a-col :span="2">
                  <h5>{{ $t("Back Side") }}</h5>
                </a-col>
                <a-col :span="22">
                  <upload-files
                    :size="uiSize"
                    :file.sync="fileList4BackDriverLicense"
                    proofOfIdentity="ProofOfIdentity"
                    fileType="individual_id_dl_back"
                  ></upload-files>
                </a-col>
              </a-row>
            </div>
          </a-col>
        </a-row>
        <p></p>
        <a-row :gutter="16">
          <a-col :span="24">
            <h4>{{ $t("Selfie") }}</h4>
            <h5>
              {{ $t("selfie_subtitle") }}
            </h5>
            <upload-files
              :size="uiSize"
              :file.sync="fileList4Selfie"
              fileType="individual_id_selfie"
            ></upload-files>
          </a-col>
        </a-row>
        <p></p>
        <a-row :gutter="16">
          <a-col :span="24">
            <h4>{{ $t("Residence") }}</h4>
            <h5>
              {{ $t("residence_subtitle") }}
            </h5>
            <upload-files
              :size="uiSize"
              :file.sync="fileList4Residence"
              fileType="individual_id_residence"
            ></upload-files>
          </a-col>
        </a-row>
      </a-card>
      <btm-step-nav
        ref="btmStepNav"
        :passed="valid && fileValid"
        :loading="isLoading"
        @submitForm="submitForm"
      ></btm-step-nav>
    </ValidationObserver>
  </section>
</template>

<script>
import { mapState } from "vuex";
import {
  Card,
  Form,
  Button,
  Input,
  Row,
  Col,
  Upload,
  Icon,
  Radio
} from "ant-design-vue";
import { MESSAGES } from "@veriff/incontext-sdk";
import BtmStepNav from "@/components/BtmStepNavIndividual";
import UploadFiles from "@/components/UploadFiles";
import PhotoExample from "@/components/PhotoExample";
import {
  getVeriffStatus,
  getIdentification,
  submitIdentification
} from "@/api/kyc";
import { displayErrors } from "@/utils";
import { initVeriff, createVeriffFrame } from "../utils";

const PREFIX_ORIGIN = "__origin-";

export default {
  name: "Identification",
  data() {
    return {
      veriffStatus: "",
      veriffId: "veriff-root",
      veriffFrame: null,
      passport: "",
      fileList4Identity: [],
      fileList4Selfie: [],
      fileList4Residence: [],
      fileList4FrontDriverLicense: [],
      fileList4BackDriverLicense: [],
      isLoading: false,
      immediate: false,
      identityType: "passport" // passport or dl
    };
  },
  components: {
    [Card.name]: Card,
    [Button.name]: Button,
    [Input.name]: Input,
    [Form.name]: Form,
    [Form.Item.name]: Form.Item,
    [Row.name]: Row,
    [Col.name]: Col,
    [Upload.name]: Upload,
    [Icon.name]: Icon,
    [Radio.name]: Radio,
    [Radio.Group.name]: Radio.Group,
    BtmStepNav,
    UploadFiles,
    PhotoExample
  },
  computed: {
    ...mapState("KYC", {
      enableVeriff: "enableVeriff4Individual"
    }),
    ...mapState({
      uiSize: state => state.clientInfo.uiSize
    }),
    fileValid() {
      if (this.enableVeriff) {
        return this.veriffStatus === MESSAGES.FINISHED;
      } else {
        // 有一个文件没有上传就为false
        return this.fileList4Selfie.length === 0 ||
          this.fileList4Residence.length === 0 ||
          (this.identityType === "passport"
            ? this.fileList4Identity.length === 0
            : this.fileList4FrontDriverLicense.length === 0 ||
              this.fileList4BackDriverLicense.length === 0)
          ? false
          : true;
      }
    }
  },
  watch: {
    veriffStatus(status) {
      if (this.enableVeriff && status === MESSAGES.FINISHED) {
        this.$refs?.btmStepNav?.nextStep?.(true);
      }
    }
  },
  methods: {
    resolveState({ errors, pending, valid }) {
      if (errors[0]) {
        return "error";
      }
      if (pending) {
        return "validating";
      }
      if (valid) {
        return "success";
      }
      return "";
    },
    setupFileList(type, name) {
      if (!name) return;

      this[`fileList4${type}`] = [
        { uid: `${PREFIX_ORIGIN}${Date.now()}`, name, status: "done" }
      ];
    },
    appendFileList(formData, type, name) {
      this[`fileList4${type}`]
        .filter(({ uid }) => {
          if (uid) {
            return !uid.startsWith(PREFIX_ORIGIN);
          } else {
            return true;
          }
        })
        .forEach(({ file_path }) => {
          formData.append(name, file_path);
        });
    },
    async submitIdentification() {
      const formData = new FormData();
      formData.append("id_no", this.passport);
      formData.append("id_type", this.identityType);

      switch (this.identityType) {
        case "passport": {
          this.appendFileList(formData, "Identity", "id_file");
          break;
        }
        case "dl": {
          this.appendFileList(formData, "FrontDriverLicense", "id_file");
          break;
        }
      }
      this.appendFileList(formData, "BackDriverLicense", "id_back_file");
      this.appendFileList(formData, "Selfie", "selfie_file");
      this.appendFileList(formData, "Residence", "proof_of_residence_file");

      await submitIdentification(formData);
    },
    async fetchIdentification() {
      const data = await getIdentification();
      this.passport = data.id_no || "";
      this.identityType = data.id_type || "passport";

      if (data.id_type === "passport") {
        this.setupFileList("Identity", data.id_file_original_name);
      } else {
        this.setupFileList("FrontDriverLicense", data.id_file_original_name);
      }
      this.setupFileList("BackDriverLicense", data.id_back_file_original_name);
      this.setupFileList("Selfie", data.selfie_file_original_name);
      this.setupFileList(
        "Residence",
        data.proof_of_residence_file_original_name
      );

      this.immediate = true;
    },
    async submitForm(nextStep) {
      try {
        this.isLoading = true;
        // await this.submitIdentification();
        nextStep(true);
        this.isLoading = false;
      } catch (err) {
        nextStep(false);
        displayErrors(err);
        this.isLoading = false;
      }
    },
    async fetchData() {
      try {
        const { status, url } = await getVeriffStatus();
        this.veriffFrame = createVeriffFrame(url, msg => {
          this.veriffStatus = msg;
        });
      } catch (err) {
        displayErrors(err);
      }
    }
  },
  created() {
    // this.fetchData();
  },
  mounted() {},
  activated() {
    initVeriff({
      id: this.veriffId,
      handler: (url = "") => {
        // console.log("init veriff with url: %o", url);
        this.veriffFrame = createVeriffFrame(url, msg => {
          this.veriffStatus = msg;
        });
      }
    });
  },
  deactivated() {
    //
  }
};
</script>
